import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react'
import { useGuildProjects } from '@/services/GuildProjectService'
import {
  GuildProject,
  GuildProjectAsset,
  GuildProjectEpisode,
  GuildProjectFlow,
  Maybe,
  TorchProject,
} from '@/types/codegen-federation'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'

type FilmmakerDashboardContextType = {
  projects?: GuildProject[]
  selectedProject?: GuildProject
  loading: boolean
  refetchProjects: () => void
  selectedEpisode?: GuildProjectEpisode
  setSelectedEpisode?: (e?: GuildProjectEpisode) => void
  selectedVersion?: Maybe<GuildProjectAsset>
  flow?: GuildProjectFlow
  handleAdminSearch?: (name?: string) => void
  admin: boolean
  pendingInvites?: TorchProject[]
}

interface Props {
  projects?: GuildProject[]
  projectId?: string
  versionId?: string
  projectFlow?: GuildProjectFlow
  pendingInvites?: TorchProject[]
}

const FilmmakerDashboardContext = createContext<FilmmakerDashboardContextType | null>(null)

export const useFilmmakerDashboard = () => {
  return useContext(FilmmakerDashboardContext) as FilmmakerDashboardContextType
}

export const FilmmakerDashboardProvider = ({
  children,
  projects: ssProjects,
  projectId,
  versionId,
  projectFlow,
  pendingInvites: ssPendingInvites,
}: PropsWithChildren & Props) => {
  const router = useSafeRouter()

  const flow = useMemo(() => projectFlow, [projectFlow])

  const [loading, setLoading] = useState(false)
  const [selectedEpisode, setSelectedEpisode] = useState<GuildProjectEpisode>()
  const [adminProjects, setAdminProjects] = useState<GuildProject[]>()
  const [hasFilmmakerAdmin, setHasFilmmakerAdmin] = useState(false)
  const getProjects = useGuildProjects()

  const { selectedProject, selectedVersion, pendingInvites, allProjects } = useMemo(() => {
    const all = [...(ssProjects ?? []), ...(adminProjects ?? [])]
    const selectedProject = all?.find((p) => p.id === projectId)
    const assets = selectedProject?.projectAssets
    const selectedVersion = assets?.find((a) => a?.id === versionId)
    return {
      selectedProject,
      selectedVersion,
      pendingInvites: ssPendingInvites,
      allProjects: all,
    }
  }, [ssProjects, projectId, versionId, ssPendingInvites, adminProjects])

  const refetchProjects = useCallback(async () => {
    setLoading(true)
    await router.replace(router.asPath)
    setTimeout(() => {
      setLoading(false)
    }, 1000)
  }, [setLoading, router])

  const handleAdminSearch = useCallback(
    async (name?: string) => {
      if (!name) return
      setLoading(true)
      const adminProjects = await getProjects({
        variables: {
          name,
        },
      })
      if (adminProjects?.data?.getGuildProjects) {
        setAdminProjects(adminProjects.data.getGuildProjects)
        if (adminProjects.data.getGuildProjects?.length > 0) {
          setHasFilmmakerAdmin(true)
        }
      }
      setLoading(false)
    },
    [getProjects, setLoading, setAdminProjects],
  )

  const value = useMemo(() => {
    return {
      projects: allProjects,
      selectedProject,
      loading,
      refetchProjects,
      selectedEpisode,
      setSelectedEpisode,
      selectedVersion,
      flow,
      handleAdminSearch,
      admin: hasFilmmakerAdmin,
      pendingInvites,
    }
  }, [
    selectedProject,
    selectedEpisode,
    selectedVersion,
    loading,
    flow,
    refetchProjects,
    setSelectedEpisode,
    handleAdminSearch,
    hasFilmmakerAdmin,
    allProjects,
    pendingInvites,
  ])
  return <FilmmakerDashboardContext.Provider value={value}>{children}</FilmmakerDashboardContext.Provider>
}
